import React, { useCallback, useRef, useState } from 'react';
import {
  TIMELINE_HEIGHT_PX,
  SNAP_MINUTES,
  DayTimelineRequest,
  clampRangeToDay,
  formatTime,
  hourRateColor,
  msToY,
  startOfDay,
  toEpochMs,
  yToMs,
} from './dayTimelineUtils';

export interface TimeRange {
  startMs: number;
  endMs: number;
}

interface DayTimelineProps {
  day: Date;
  requests: DayTimelineRequest[];
  selectedRange?: TimeRange | null;
  onRangeChange?: (range: TimeRange | null) => void;
  interactive?: boolean;
  disabled?: boolean;
  title?: string;
}

const HOUR_ROWS = Array.from({ length: 24 }, (_, hour) => hour);

export default function DayTimeline({
  day,
  requests,
  selectedRange = null,
  onRangeChange,
  interactive = false,
  disabled = false,
  title = 'After Hours Requests Timeline',
}: DayTimelineProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const dragAnchor = useRef<number | null>(null);

  const getYFromEvent = useCallback((clientY: number) => {
    const track = trackRef.current;
    if (!track) return 0;
    const rect = track.getBoundingClientRect();
    return Math.max(0, Math.min(TIMELINE_HEIGHT_PX, clientY - rect.top));
  }, []);

  const updateRangeFromDrag = useCallback(
    (clientY: number, anchorMs: number) => {
      if (!onRangeChange) return;
      const currentMs = yToMs(getYFromEvent(clientY), day);
      const range = clampRangeToDay(
        Math.min(anchorMs, currentMs),
        Math.max(anchorMs, currentMs),
        day,
      );
      onRangeChange(range);
    },
    [day, getYFromEvent, onRangeChange],
  );

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!interactive || disabled || !onRangeChange) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    const anchorMs = yToMs(getYFromEvent(event.clientY), day);
    dragAnchor.current = anchorMs;
    setDragging(true);
    onRangeChange(clampRangeToDay(anchorMs, anchorMs + SNAP_MINUTES * 60 * 1000, day));
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging || dragAnchor.current === null) return;
    event.preventDefault();
    updateRangeFromDrag(event.clientY, dragAnchor.current);
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    event.preventDefault();
    if (dragAnchor.current !== null) {
      updateRangeFromDrag(event.clientY, dragAnchor.current);
    }
    dragAnchor.current = null;
    setDragging(false);
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  const selectionTop = selectedRange ? msToY(selectedRange.startMs, day) : 0;
  const selectionHeight = selectedRange
    ? Math.max(msToY(selectedRange.endMs, day) - selectionTop, 6)
    : 0;

  return (
    <div
      className="ah-card ah-timeline-card"
      style={{
        padding: 0,
        opacity: disabled ? 0.55 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        filter: disabled ? 'grayscale(15%)' : 'none',
        transition: 'opacity 0.2s ease',
      }}
    >
      <div style={{ display: 'flex', height: TIMELINE_HEIGHT_PX + 40 }}>
        <div style={{ width: 60, borderRight: '1px solid var(--border)', flexShrink: 0 }} className="ah-timeline-label">
          <div
            style={{
              height: 40,
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 600,
              fontSize: 12,
            }}
          >
            Hour
          </div>
          {HOUR_ROWS.map((hour) => (
            <div
              key={hour}
              style={{
                height: TIMELINE_HEIGHT_PX / 24,
                borderBottom: hour < 23 ? '1px solid #f1f5f9' : undefined,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                fontSize: 10,
                paddingTop: 2,
              }}
            >
              {hour.toString().padStart(2, '0')}:00
            </div>
          ))}
        </div>

        <div style={{ flex: 1, position: 'relative', minWidth: 0 }} className="ah-timeline-request">
          <div
            style={{
              height: 40,
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 600,
              fontSize: 12,
              padding: '0 8px',
              textAlign: 'center',
            }}
          >
            {title}
          </div>

          <div
            ref={trackRef}
            style={{
              position: 'relative',
              height: TIMELINE_HEIGHT_PX,
              background: 'linear-gradient(to bottom, #f7fafc 0%, #edf2f7 100%)',
              cursor: interactive ? 'crosshair' : 'default',
              touchAction: interactive ? 'none' : 'auto',
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            {HOUR_ROWS.map((hour) => (
              <div
                key={`grid-${hour}`}
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: (hour / 24) * TIMELINE_HEIGHT_PX,
                  height: TIMELINE_HEIGHT_PX / 24,
                  borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
                  pointerEvents: 'none',
                  backgroundColor: hourRateColor(hour),
                  opacity: 0.12,
                }}
              />
            ))}

            {requests.map((request) => {
              const start = toEpochMs(request.startDateTime);
              const end = toEpochMs(request.endDateTime);
              const dayStart = startOfDay(day).getTime();
              const dayEnd = dayStart + 24 * 60 * 60 * 1000;
              const visibleStart = Math.max(start, dayStart);
              const visibleEnd = Math.min(end, dayEnd);
              const top = msToY(visibleStart, day);
              const height = Math.max(msToY(visibleEnd, day) - top, 6);
              const hour = new Date(visibleStart).getHours();

              return (
                <div
                  key={request.id}
                  title={`${formatTime(visibleStart)} – ${formatTime(visibleEnd)}`}
                  style={{
                    position: 'absolute',
                    left: 8,
                    right: 8,
                    top,
                    height,
                    borderRadius: 4,
                    backgroundColor: hourRateColor(hour),
                    opacity: 0.85,
                    border: '1px solid rgba(0,0,0,0.1)',
                    pointerEvents: 'none',
                    padding: '2px 6px',
                    overflow: 'hidden',
                    fontSize: 10,
                    fontWeight: 600,
                    color: '#1a202c',
                  }}
                >
                  {formatTime(visibleStart)} – {formatTime(visibleEnd)}
                </div>
              );
            })}

            {interactive && selectedRange && (
              <div
                style={{
                  position: 'absolute',
                  left: 6,
                  right: 6,
                  top: selectionTop,
                  height: selectionHeight,
                  borderRadius: 6,
                  backgroundColor: 'rgba(49, 130, 206, 0.35)',
                  border: '2px solid var(--primary, #3182ce)',
                  pointerEvents: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#1a365d',
                }}
              >
                {formatTime(selectedRange.startMs)} – {formatTime(selectedRange.endMs)}
              </div>
            )}
          </div>
        </div>
      </div>

      {interactive && (
        <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', fontSize: 12 }} className="muted">
          Drag on the timeline to select your requested time. Snaps to {SNAP_MINUTES}-minute intervals.
        </div>
      )}

      {requests.length > 0 && (
        <div style={{ padding: 16, borderTop: '1px solid var(--border)' }}>
          <h4 style={{ marginBottom: 12, fontSize: 14, fontWeight: 600 }}>Existing Requests</h4>
          <div style={{ display: 'grid', gap: 8, maxHeight: 200, overflowY: 'auto' }}>
            {requests.map((request) => {
              const start = toEpochMs(request.startDateTime);
              const end = toEpochMs(request.endDateTime);
              const hour = new Date(start).getHours();
              return (
                <div
                  key={request.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 8,
                    backgroundColor: '#f8f9fa',
                    borderRadius: 4,
                    fontSize: 12,
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 500 }}>
                      {formatTime(start)} – {formatTime(end)}
                    </div>
                    <div className="muted">
                      {request.areas?.join(', ') || 'No areas specified'}
                      {request.requesterUserId ? ` • ${request.requesterUserId}` : ''}
                    </div>
                    {request.reason && (
                      <div className="muted" style={{ fontSize: 11 }}>
                        {request.reason}
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      padding: '4px 8px',
                      borderRadius: 4,
                      backgroundColor: hourRateColor(hour),
                      color: 'white',
                      fontSize: 10,
                      fontWeight: 600,
                    }}
                  >
                    {hour >= 18 || hour < 6 ? 'High' : hour >= 12 ? 'Med' : 'Low'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
